import React from "react";
import { fmtCurrency, fmtNumber } from "@/lib/api";
import { chartColors } from "@/lib/chartTheme";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { TrendingUp, ArrowUpRight, ArrowDownRight, GitCompareArrows } from "lucide-react";

const BAR_MARGIN = { left: 10, right: 10 };
const BAR_RADIUS = [4, 4, 0, 0];

function Empty({ msg }) {
  return (
    <div className="h-[120px] flex items-center justify-center text-sm text-muted-foreground text-center px-4">
      {msg}
    </div>
  );
}

export default function ForecastMovement({ forecast, movement, theme }) {
  const c = chartColors(theme);
  const data = forecast || [];
  const hasForecast = data.some((m) => m.value > 0);
  const mv = movement || {};
  const net = mv.net_change ?? 0;

  const tooltipStyle = {
    backgroundColor: theme === "dark" ? "#0d1526" : "#ffffff",
    border: `1px solid ${c.grid}`,
    borderRadius: 8,
    color: theme === "dark" ? "#fff" : "#0f172a",
    fontSize: 12,
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Forecast by close month */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" /> Forecast by Close Month
            <span className="text-xs font-normal text-muted-foreground ml-1">· next 6 months, as of now</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hasForecast ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data} margin={BAR_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={c.grid} />
                <XAxis dataKey="label" fontSize={11} stroke={c.axis} />
                <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} fontSize={11} stroke={c.axis} />
                <Tooltip formatter={(v) => fmtCurrency(v)} contentStyle={tooltipStyle} />
                <Legend />
                <Bar dataKey="value" name="Pipeline" fill={c.primary} radius={BAR_RADIUS} />
                <Bar dataKey="weighted" name="Weighted" fill={c.won} radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Empty msg="No open deals with an upcoming close date." />
          )}
        </CardContent>
      </Card>

      {/* Pipeline movement (period) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <GitCompareArrows className="w-4 h-4 text-primary" /> Pipeline Movement
            <span className="text-xs font-normal text-muted-foreground ml-1">· in period</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-lg border border-border p-3">
            <p className="text-xs text-muted-foreground">Created</p>
            <p className="text-lg font-bold text-foreground">{fmtCurrency(mv.created_value)}</p>
            <p className="text-[11px] text-muted-foreground/70">{fmtNumber(mv.created_count)} deals</p>
          </div>
          <div className="rounded-lg border border-border p-3">
            <p className="text-xs text-muted-foreground">Closed</p>
            <p className="text-lg font-bold text-foreground">{fmtCurrency(mv.closed_value)}</p>
            <p className="text-[11px] text-muted-foreground/70">{fmtNumber(mv.closed_count)} deals</p>
          </div>
          <div className={`rounded-lg border p-3 ${net >= 0 ? "border-emerald-500/30 bg-emerald-500/5" : "border-primary/30 bg-primary/5"}`}>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              Net Pipeline Change
              {net >= 0 ? (
                <ArrowUpRight className="w-3.5 h-3.5 text-emerald-600" />
              ) : (
                <ArrowDownRight className="w-3.5 h-3.5 text-primary" />
              )}
            </p>
            <p className={`text-lg font-bold ${net >= 0 ? "text-emerald-600" : "text-primary"}`}>
              {net >= 0 ? "+" : "−"}
              {fmtCurrency(Math.abs(net))}
            </p>
            <p className="text-[11px] text-muted-foreground/70">created − closed</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
